<script lang="ts">
export default {
  name: 'GenericFilter',
};
</script>

<script setup lang="ts">
import FilterButton from '@/components/FilterButton.vue';
import { LabelFilterType } from '@/types/components/FilterType';
import { ref, computed, watch } from 'vue';

const props = withDefaults(defineProps<{
  modelValue: Array<string | number>
  types: Array<LabelFilterType<string | number>>
  title: string
  staticChipTitle?: boolean
  chipTitleField?: string
  dataTest?: string
  dataTestConfirm?: string
  dataTestClear?: string
  dataTestId?: string
}>(), {
  staticChipTitle: false,
  chipTitleField: undefined,
  dataTest: undefined,
  dataTestConfirm: undefined,
  dataTestClear: undefined,
  dataTestId: undefined
});

const emit = defineEmits<{
  'update:modelValue': [Array<string | number>];
  clear: [];
}>();

const dirty = ref(false);
const selectedTypes = ref<Array<string | number>>([]);

const chipTitle = computed(() => {
  if (props.chipTitleField) {
    if (props.modelValue.length === 0 || props.modelValue.length > 2) {
      return props.title;
    }
    const field = props.chipTitleField;
    return props.types
      .filter(t => props.modelValue.some(st => st === t.value))
      .slice(0, 2)
      .map(t => Reflect.get(t, field, null)).join(', ');
  }

  if (!props.staticChipTitle && selectedTypes.value.length === 1) {
    return String(selectedTypes.value[0]);
  }
  return props.title;
});

watch(() => props.modelValue, (value) => {
  selectedTypes.value = [...value];
});

watch(() => selectedTypes.value, () => {
  dirty.value = true;
});

function clear() {
  emit('clear');
  selectedTypes.value = [];
}

function revert() {
  selectedTypes.value = [...props.modelValue];
  dirty.value = false;
}

function open() {
  selectedTypes.value = [...props.modelValue];
}

function save() {
  if (dirty.value) {
    dirty.value = false;
    emit('update:modelValue', [...selectedTypes.value]);
  }
  return true;
}

function selectAll() {
  selectedTypes.value = props.types.map(x => x.value);
}
</script>

<template>
  <FilterButton :title="chipTitle" :active="props.modelValue.length > 0" :data-test-activator="dataTest" :data-testid="dataTestId"
                @close="revert" @open="open">
    <template #menu="{ close }">
      <div class="menu">
        <h4 class="tw:py-0 tw:px-4">{{ title }}</h4>

        <div :class="types.length > 20 ? 'scroll-list' : 'columns'">
          <label class="filter-item" v-for="type in types" :key="type.label">
            <input type="checkbox" v-model="selectedTypes" :value="type.value" :data-test="`filter-item-${type.label.replaceAll(' ', '')}`"/>
            <span>{{ type.label }}</span>
          </label>
        </div>

        <div class="buttons">
          <v-btn variant="outlined" size="small" @click="clear" :data-test="dataTestClear">Clear</v-btn>
          <slot name="options"></slot>
          <span>
            <v-btn class="tw:mr-2" size="small" color="secondary" variant="flat"
                   @click="selectAll" data-test="filter-coverage-type-select-all">Select All</v-btn>
            <v-btn size="small" variant="flat" color="primary" @click="() => {save();close();}" :data-testid="dataTestId + '-apply'">Apply</v-btn>
          </span>
        </div>
      </div>
    </template>
  </FilterButton>
</template>

<style scoped>
.menu {
  padding: 1rem 0 0 0;
  min-width: 500px;
}

.filter-item {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 0.5rem;
  align-content: center;
  align-items: center;
  padding: 0.2rem 0;
  cursor: pointer;
  user-select: none;
}

.menu > .buttons {
  padding: 1rem;
  border-top: 1px solid var(--v-border-base);
  background: var(--v-gray-lighten-5);
  display: flex;
  justify-content: space-between;
}

.columns {
  padding: 0 1rem;
  margin: 0.5rem 0 1rem 0;
  display: grid;
  grid-template-columns: repeat(2, auto);
  grid-column-gap: 4rem;
}

.scroll-list {
  max-height: 300px;
  overflow-y: scroll;
  margin: 0.5rem 1rem 1rem;
  display: grid;
  grid-template-columns: repeat(2, auto);
  grid-column-gap: 4rem;
  border: 1px solid var(--v-border-base);
  padding: 0.5rem;
}
</style>
